import { useEffect } from 'react';
import { Button, Card, Flex, Typography, Row, Col } from 'antd';
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import EstimateItemsTable from '../../components/Estimate/ItemsTable';
import CalculationList from '../../components/Estimate/CalculationList';
import EstimateAlertBar from '../../components/Estimate/AlertBar';
import { getEstimateNumber } from '../../lib/modelHelpers/estimate';
import { formatTextToReact } from '../../lib/utils/textFormatting';
import { EstimateLayoutQuery$data } from '../../layout/Estimate/__generated__/EstimateLayoutQuery.graphql';

import styles from './index.module.less';
import { getCustomerName } from '../../lib/modelHelpers/customer';

const { Title, Text } = Typography;

function EstimateShowPage(props: { estimate: NonNullable<EstimateLayoutQuery$data['estimate']> }) {
  const estimate = props.estimate;

  useEffect(() => {
    document.title = 'Angebot ' + getEstimateNumber(estimate);
  }, [estimate]);

  return (
    <div className={styles.container}>
      {/* Status Alert Bar */}
      <EstimateAlertBar estimate={estimate} />

      {/* Estimate Header */}
      <header className={styles.estimateHeader}>
        <div className={styles.estimateHeaderContent}>
          <FileTextOutlined className={styles.estimateIcon} />
          <div>
            <Text className={styles.estimateLabel}>Angebot</Text>
            <Title level={2} className={styles.estimateNumber}>
              {getEstimateNumber(estimate)}
            </Title>
            <div className={styles.estimateDates}>
              <Text className={styles.dateText}>
                <span className={styles.dateLabel}>Angebotsdatum:</span>{' '}
                {dayjs(estimate.estimateDate).format('DD.MM.YYYY')}
              </Text>
              <Text className={styles.dateText}>
                <span className={styles.dateLabel}>Gültig bis:</span>{' '}
                {dayjs(estimate.validityDate).format('DD.MM.YYYY')}
              </Text>
            </div>
          </div>
        </div>
        {(estimate.estimatePdf || estimate.scopeOfWorkPdf) && (
          <Flex wrap='wrap' gap='middle'>
            {estimate.estimatePdf && (
              <Button
                type='primary'
                size='large'
                icon={<DownloadOutlined />}
                onClick={() => window.open(estimate.estimatePdf!, '_blank')}
                className={styles.downloadButton}
              >
                PDF herunterladen
              </Button>
            )}
            {estimate.scopeOfWorkPdf && (
              <Button
                size='large'
                icon={<DownloadOutlined />}
                onClick={() => window.open(estimate.scopeOfWorkPdf!, '_blank')}
                className={styles.downloadButton}
              >
                Leistungsverzeichnis herunterladen
              </Button>
            )}
          </Flex>
        )}
      </header>

      {/* Combined Company and Customer Info */}
      <Card className={`${styles.card} ${styles.documentHeader}`}>
        <Row gutter={[24, 24]}>
          {/* Company Info - Left Column */}
          <Col xs={24} md={12}>
            <div className={styles.companySection}>
              {estimate.company?.logoUrl && (
                <img
                  src={estimate.company.logoUrl}
                  alt={estimate.company.companyName || 'Company Logo'}
                  className={styles.companyLogo}
                />
              )}
              <div className={styles.companyInfo}>
                <strong className={styles.companyName}>{estimate.company?.companyName}</strong>
                <div className={styles.companyAddress}>
                  <div>{estimate.company?.streetAddress}</div>
                  <div>
                    {estimate.company?.zip} {estimate.company?.city}
                  </div>
                </div>
                {(estimate.company?.email || estimate.company?.phone) && (
                  <div className={styles.companyContact}>
                    {estimate.company?.email && <div>E-Mail: {estimate.company.email}</div>}
                    {estimate.company?.phone && <div>Tel: {estimate.company.phone}</div>}
                    {estimate.company?.fax && <div>Fax: {estimate.company.fax}</div>}
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* Customer Info - Right Column */}
          <Col xs={24} md={12}>
            <div className={styles.customerSection}>
              <div className={styles.customerLabel}>Angebot an</div>
              <div className={styles.customerInfo}>
                <strong className={styles.customerName}>
                  {getCustomerName(estimate.customer)}
                </strong>
                {estimate.customer?.streetAddress && (
                  <div className={styles.customerAddress}>{estimate.customer.streetAddress}</div>
                )}
                {(estimate.customer?.zip || estimate.customer?.city) && (
                  <div className={styles.customerAddress}>
                    {estimate.customer?.zip} {estimate.customer?.city}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Combined Items & Calculation Section */}
      <Card
        className={`${styles.card} ${styles.itemsCalculationCard} ${!estimate.introductionText && !estimate.closingText ? styles.roundedCard : ''}`}
      >
        {/* Introduction Text */}
        {estimate.introductionText && (
          <div className={styles.textContent}>{formatTextToReact(estimate.introductionText)}</div>
        )}

        {/* Items Section */}
        <div className={styles.itemsSection}>
          <EstimateItemsTable items={estimate.items} />
        </div>

        {/* Calculation Section */}
        <div className={styles.calculationSection}>
          <Row gutter={[24, 24]} className={styles.calculationRow}>
            <Col xs={24} lg={16}>
              {/* Empty column for spacing on large screens */}
            </Col>
            <Col xs={24} lg={8}>
              <CalculationList estimateItems={estimate.items} />
            </Col>
          </Row>
        </div>

        {/* Closing Text */}
        {estimate.closingText && (
          <div className={styles.textContent}>{formatTextToReact(estimate.closingText)}</div>
        )}
      </Card>

      {/* Additional Company Info */}
      {(estimate.company?.taxNumber || estimate.company?.vatId || estimate.company?.iban) && (
        <Card className={`${styles.card} ${styles.additionalInfoCard}`}>
          <Row gutter={[24, 24]}>
            {/* Tax and Registration Info */}
            {(estimate.company?.taxNumber ||
              estimate.company?.vatId ||
              estimate.company?.registerCourt ||
              estimate.company?.companyRegistrationNumber) && (
              <Col xs={24} md={12}>
                <div className={styles.infoSection}>
                  {estimate.company?.taxNumber && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Steuernummer</span>
                      <span className={styles.infoValue}>{estimate.company.taxNumber}</span>
                    </div>
                  )}
                  {estimate.company?.vatId && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>USt-IdNr.</span>
                      <span className={styles.infoValue}>{estimate.company.vatId}</span>
                    </div>
                  )}
                  {estimate.company?.registerCourt && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Registergericht</span>
                      <span className={styles.infoValue}>{estimate.company.registerCourt}</span>
                    </div>
                  )}
                  {estimate.company?.companyRegistrationNumber && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Handelsregisternummer</span>
                      <span className={styles.infoValue}>
                        {estimate.company.companyRegistrationNumber}
                      </span>
                    </div>
                  )}
                  {estimate.company?.managingDirectors &&
                    estimate.company.managingDirectors.length > 0 && (
                      <div className={styles.infoItemInline}>
                        <span className={styles.infoLabel}>Geschäftsführung:</span>
                        <span className={styles.infoValue}>
                          {estimate.company.managingDirectors.map((md) => md?.name).join(', ')}
                        </span>
                      </div>
                    )}
                </div>
              </Col>
            )}

            {/* Banking Info */}
            {estimate.company?.iban && (
              <Col xs={24} md={12}>
                <div className={`${styles.infoSection} ${styles.infoSectionCompact}`}>
                  <div className={styles.infoSectionTitle}>Bankverbindung</div>
                  {estimate.company?.iban && (
                    <div className={styles.infoItemInline}>
                      <span className={styles.infoLabel}>IBAN:</span>
                      <span className={styles.infoValue}>{estimate.company.iban}</span>
                    </div>
                  )}
                  {estimate.company?.bic && (
                    <div className={styles.infoItemInline}>
                      <span className={styles.infoLabel}>BIC:</span>
                      <span className={styles.infoValue}>{estimate.company.bic}</span>
                    </div>
                  )}
                </div>
              </Col>
            )}
          </Row>
        </Card>
      )}
    </div>
  );
}

export default EstimateShowPage;
